import {StyleSheet, Text, View} from "react-native";
import {Image} from "expo-image";
import {AppDataMediaItem} from "@/data/app-data-interfaces";
import {ThemeType} from "@/theming/baseTheme";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {formatDuration} from "@/lib/utils";
import {PlayButton} from "@/components/common/PlayButton";
import {BaseCard} from "@/components/common/BaseCard";
import {useCommonStyles} from "@/hooks/useCommonStyles";
import {OpenButton} from "@/components/common/OpenButton";

export default function MediaCard({item, onPressed}: { item: AppDataMediaItem, onPressed: (itemId: string) => void }) {
    const {theme} = useCurrentTheme();
    const commonStyles = useCommonStyles();
    const styles = createStyles(theme);

    const blurhash = item.imageBlurhash && item.imageBlurhash !== 'undefined' ? item.imageBlurhash : 'LKO2?U%2Tw=w]~RBVZRi};RPxuwH';

    return (
        <BaseCard
            style={styles.videoCard}
            onPress={() => onPressed(item.id)}
            activeOpacity={0.8}
        >
            <View style={styles.videoThumbnail}>
                <Image
                    source={{uri: item.image.md}}
                    placeholder={{blurhash}}
                    style={styles.thumbnailImage}
                    contentFit="cover"
                />
                <View style={styles.playButton}>
                    {(item.type === 'video' || item.type === 'audio') ? (
                        <PlayButton size={50} onPress={() => onPressed(item.id)}/>
                    ) : (
                        <OpenButton size={50} onPress={() => onPressed(item.id)}/>
                    )}
                </View>
                {item.duration !== 0 && (
                    <View style={styles.durationBadge}>
                        <Text style={styles.durationText}>
                            {formatDuration(item.duration)}
                        </Text>
                    </View>
                )}
            </View>
            <View style={styles.videoInfo}>
                <Text style={[commonStyles.title, styles.videoTitle]} numberOfLines={1}>
                    {item.name}
                </Text>
                <Text style={[commonStyles.subtitle, styles.videoDescription]} numberOfLines={1}>
                    {item.description}
                </Text>
            </View>
        </BaseCard>
    );
}

const createStyles = (theme: ThemeType) => StyleSheet.create({
    // Video Cards
    videosScroll: {
        paddingHorizontal: theme.spacing.lg,
    },
    videoCard: {
        width: 260,
        borderWidth: 1,
        borderColor: theme.colors.border,
        overflow: 'hidden',
        marginBottom: 0, // Override BaseCard default margin
        marginHorizontal: 0, // Override BaseCard default margin
    },
    videoThumbnail: {
        width: '100%',
        height: 140,
        position: 'relative',
        backgroundColor: theme.colors.textSecondary,
    },
    thumbnailImage: {
        width: '100%',
        height: '100%',
    },
    playButton: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        alignItems: 'center',
        justifyContent: 'center',
        transform: [{translateX: -25}, {translateY: -25}],
    },
    playIcon: {
        color: theme.colors.background,
        fontSize: 18,
        fontWeight: 'bold',
        marginLeft: 3, // Slight offset for better visual alignment
    },
    durationBadge: {
        position: 'absolute',
        bottom: theme.spacing.xs,
        right: theme.spacing.xs,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        paddingHorizontal: theme.spacing.xs,
        paddingVertical: 2,
        borderRadius: theme.borderRadius.sm,
    },
    durationText: {
        color: 'white',
        fontSize: theme.fontSize.xs,
        fontWeight: '600',
        lineHeight: 14,
    },
    videoInfo: {
        padding: theme.spacing.sm,
    },
    videoTitle: {
        fontSize: theme.fontSize.sm,
        marginBottom: theme.spacing.xs,
        lineHeight: theme.fontSize.sm * 1.3,
    },
    videoDescription: {
        lineHeight: theme.fontSize.xs * 1.4,
    },
});
